import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useOnboarding } from "../context/OnboardingContext";
import { ONBOARDING_PATH } from "../lib/onboarding";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";

/**
 * "You skipped setting up — here's the way back in." Renders nothing at all
 * unless the server says this player should see it (issue #289) — see
 * `OnboardingContext`'s own comment for what `showResumePrompt` means and
 * why it can expire.
 *
 * Issue #760 fix round — extracted out of `pages/Dashboard.jsx` (where it
 * used to be a file-local, unexported function) into its own module, so it
 * can be rendered from two call sites: the shipped populated dashboard
 * (`Dashboard.jsx`, unchanged position — before the hero, `variant`
 * omitted) and `RecordEmptyScreen.jsx`'s own navy chrome, which is the
 * ONLY place this prompt can ever actually fire (`showResumePrompt` is true
 * only while the account is still empty — see `OnboardingContext`'s own
 * doc), so the drawing's own note calls it "the one prompt the record
 * carries" (`EmptyRecord.dc.html`'s `set-up-not-finished` commentary
 * block).
 *
 * PR #846 fix round, B2 — the record's own call site needs the DRAWN
 * variant: a white card with a 3px navy left rule, Manrope 700/17px title,
 * and a navy-outlined link — never the shipped gold-bordered cream `Card`,
 * which would put a SECOND gold-adjacent element on a screen whose one
 * rule (R-003) is that `dash-log-match` is the only gold thing on it (the
 * shipped variant's border is `#D4A017`, gold, not merely gold-adjacent —
 * journey 29's own gold sweep is what caught this the first time round).
 * `variant="record"` is new and opt-in; the default renders the ORIGINAL
 * markup, byte-for-byte, so the populated dashboard (both e2e legs) is
 * unaffected. Both variants keep the same two testids and the same
 * underlying copy logic (`status?.profile_complete` swaps which body line
 * shows, and which step "Pick up where you left off" resumes at) — only
 * the wrapper and the link's own presentation differ.
 */
export function OnboardingResumePrompt({ variant = "default" }) {
  const { status, showResumePrompt } = useOnboarding();
  if (!showResumePrompt) return null;

  const step = status?.profile_complete ? "identity" : "profile";
  const bodyText = status?.profile_complete
    ? "Your profile is done. The last step is your vision as a cricketer — where you're heading, and why."
    : "Two short steps: how you play, then where you're heading. It takes about a minute.";

  if (variant === "record") {
    return (
      <div
        data-testid="dash-onboarding-resume"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 16,
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderLeft: "3px solid var(--nw-navy)",
          borderRadius: "var(--nw-radius-card)",
        }}
      >
        <div
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: 17,
            lineHeight: 1.25,
            color: "var(--nw-ink)",
          }}
        >
          Finish setting up your profile
        </div>
        <div className="nw-lede">{bodyText}</div>
        {/* Navy text on a light (`--nw-line-strong`, #CBD5E1) border — the
            drawing's own spec for this link, distinct from every kit
            `SecondaryAction` variant (`boxed` uses `--nw-ink-muted` text;
            `navy-outline` uses a full navy border and a 52px `--nw-size-
            action` min-height, not this card's drawn 48px) — built inline
            rather than forcing a fourth variant onto a component with one
            caller for this exact shape. No gold anywhere on this element. */}
        <Link
          to={`${ONBOARDING_PATH}?step=${step}`}
          data-testid="dash-onboarding-resume-link"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            minHeight: 48,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line-strong)",
            color: "var(--nw-navy)",
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontWeight: 600,
            fontSize: 14,
            borderRadius: "var(--nw-radius-control)",
            textDecoration: "none",
          }}
        >
          Pick up where you left off
        </Link>
      </div>
    );
  }

  // Default — the shipped, populated-dashboard shape. Byte-for-byte the
  // original markup (fix round B2's own instruction): neither this branch
  // nor its call site in `Dashboard.jsx` changed.
  return (
    <Card className="border-[#D4A017] bg-[#D4A0170D]" data-testid="dash-onboarding-resume">
      <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <div className="min-w-0">
          <p className="font-display font-bold text-slate-900">Finish setting up your profile</p>
          <p className="text-sm text-slate-600 mt-1">{bodyText}</p>
        </div>
        <Link to={`${ONBOARDING_PATH}?step=${step}`} className="shrink-0">
          <Button variant="outline" className="border-[#D4A017]" data-testid="dash-onboarding-resume-link">
            Pick up where you left off <ArrowRight size={14} className="ml-1" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default OnboardingResumePrompt;
